  import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
  import axios from 'axios';
  import io from 'socket.io-client';

  // Constants
  const NEW_MESSAGE_SOUND_PATH = `${process.env.PUBLIC_URL}/new-message.mp3`;
  const SOCKET_URL = 'https://barriobucks.com';
  const API_BASE_URL = 'https://barriobucks.com/api';

  // Initialize Socket.IO
  const socket = io(SOCKET_URL, {
    transports: ['websocket', 'polling'],
    withCredentials: true,
    reconnectionAttempts: 5,
    timeout: 10000,
  });

  // Initialize Axios
  const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
  });

  // Platform chips shown on threads whose confirmation texts came from an order
  const SOURCE_CHIPS = {
    GrubHub: { label: 'GHub', className: 'bg-orange-100 text-orange-700' },
    ChowNow: { label: 'CNow', className: 'bg-teal-100 text-teal-700' },
    Toast: { label: 'Toast', className: 'bg-amber-100 text-amber-800' },
    'web-order': { label: '🌯 ORDER', className: 'bg-red-600 text-white' },
    'web-order-test': { label: '🧪 TEST ORDER', className: 'bg-gray-300 text-gray-700' },
  };

  // Utility Functions
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'Unknown';
    const cleaned = ('' + phoneNumber).replace(/\D/g, '').slice(-10);
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phoneNumber;
  };

  const normalizePhoneNumber = (phoneNumber) => {
    return phoneNumber ? phoneNumber.replace(/\D/g, '') : '';
  };

  // Welcome Popup Component
  const WelcomePopup = ({ onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl">
        <h2 className="text-xl font-bold mb-4">Welcome to Txtr!</h2>
        <p className="mb-4">Your messaging hub is ready.</p>
        <button
          onClick={onClose}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Get Started
        </button>
      </div>
    </div>
  );

  // Main Txtr Component
  const Txtr = forwardRef(({
    searchTerm: initialSearchTerm,
    setIsTxtrLoaded,
    selectedThread,
    setSelectedThread,
    messages,
    setMessages,
    threads,
    setThreads
  }, ref) => {
    // State Variables
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);
    const [localSearchTerm, setLocalSearchTerm] = useState(initialSearchTerm);
    const [selectedImage, setSelectedImage] = useState(null);
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);
    const [isAudioEnabled, setIsAudioEnabled] = useState(false);
    const [showWelcomePopup, setShowWelcomePopup] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observerRef = useRef();
    const [audioContext, setAudioContext] = useState(null);
    const audioBufferRef = useRef(null);
    
    // Ref for Alert Interval
    const alertIntervalRef = useRef(null);

    // New State: Controls whether alert sounds are allowed to play
    const [alertSoundsAllowed, setAlertSoundsAllowed] = useState(true);

    // Audio Initialization
    const initializeAudio = useCallback(() => {
      if (audioContext) return; // Already initialized

      const context = new (window.AudioContext || window.webkitAudioContext)();
      setAudioContext(context);

      fetch(NEW_MESSAGE_SOUND_PATH)
        .then(response => response.arrayBuffer())
        .then(arrayBuffer => context.decodeAudioData(arrayBuffer))
        .then(audioBuffer => {
          audioBufferRef.current = audioBuffer;
        })
        .catch(error => console.error('Error loading audio:', error));
    }, [audioContext]);

    const processedMessages = useRef(new Set());

    // Play Sound
    const playSound = useCallback(() => {
      if (audioContext && audioBufferRef.current && isAudioEnabled) {
        const source = audioContext.createBufferSource();
        source.buffer = audioBufferRef.current;
        source.connect(audioContext.destination);
        source.start(0);
      }
    }, [audioContext, isAudioEnabled]);

    // Enable Audio
    const enableAudio = useCallback(() => {
      initializeAudio();
      setIsAudioEnabled(true);
    }, [initializeAudio]);

    // Intersection Observer for Infinite Scrolling
    const lastThreadElementRef = useCallback(node => {
      if (isLoading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          fetchThreads();
        }
      });
      if (node) observerRef.current.observe(node);
    }, [isLoading, hasMore]);


    const threadObserver = useRef(new IntersectionObserver((entries) => {
      entries.forEach(async entry => {
        if (entry.isIntersecting) {
          const thread = JSON.parse(entry.target.dataset.thread);
          if (!thread.customerName && !thread.customerInfo_fetching) {
            // Mark as fetching to prevent duplicate requests
            setThreads(prevThreads => prevThreads.map(t => 
              normalizePhoneNumber(t.phone_number) === normalizePhoneNumber(thread.phone_number)
                ? { ...t, customerInfo_fetching: true }
                : t
            ));
            
            const customerInfo = await fetchCustomerInfo(thread.phone_number);
            if (customerInfo && customerInfo.name) {
              setThreads(prevThreads => prevThreads.map(t => 
                normalizePhoneNumber(t.phone_number) === normalizePhoneNumber(thread.phone_number)
                  ? { ...t, customerName: customerInfo.name, customerInfo_fetching: false }
                  : t
              ));
            } else {
              // Clear fetching flag even if no customer found
              setThreads(prevThreads => prevThreads.map(t => 
                normalizePhoneNumber(t.phone_number) === normalizePhoneNumber(thread.phone_number)
                  ? { ...t, customerInfo_fetching: false }
                  : t
              ));
            }
          }
        }
      });
    }, { rootMargin: '50px' }));

    // Fetch Customer Info
    const fetchCustomerInfo = useCallback(async (phone) => {
      try {
        const response = await api.get('/customer-info', { params: { phone } });
        return response.data.customer || null;
      } catch (err) {
        console.error(`Error fetching customer info for ${phone}:`, err);
        return null;
      }
    }, []);

    // Unread Alert Component
    const UnreadAlert = ({ unreadCount, onClearAll }) => (
      <div className="bg-blue-200 p-2 flex justify-between items-center">
        <span>{unreadCount} unread</span>
        <button onClick={onClearAll} className="text-blue-600 hover:text-blue-800">
          Clear all
        </button>
      </div>
    );

    // Clear All Unread Threads
    const clearAllUnread = useCallback(async () => {
      try {
        // Mark all unread messages as read on the server
        await api.put('/mark-all-read');
        
        // Update local state
        setThreads(prevThreads => prevThreads.map(thread => ({
          ...thread,
          unread_count: 0
        })));
        
        setTotalUnreadCount(0);
        setAlertSoundsAllowed(false); // Stop alert sounds
      } catch (error) {
        console.error('Error marking all messages as read:', error);
      }
    }, [setThreads]);

    // Fetch Threads
    // const fetchThreads = useCallback(async (pageToFetch = page, searchTerm = localSearchTerm) => {
    //   if (!hasMore) return;
    //   setIsLoading(true);
    //   try {
    //     const response = await api.get('/threads', {
    //       params: { page: pageToFetch, limit: 20, search: searchTerm }
    //     });
        
    //     const newThreads = response.data.map(thread => ({
    //       ...thread,
    //       customerName: null // Initialize with null, will be fetched when visible
    //     }));
        
    //     setThreads(prevThreads => {
    //       const combined = [...prevThreads, ...newThreads];
    //       const uniqueThreadsMap = new Map(combined.map(thread => [normalizePhoneNumber(thread.phone_number), thread]));
    //       const uniqueThreads = Array.from(uniqueThreadsMap.values());
    //       return uniqueThreads.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    //     });
    
    //     setHasMore(newThreads.length === 20);
    //     setPage(pageToFetch + 1);
    //     setError(null);
    //   } catch (error) {
    //     console.error('Error fetching threads:', error);
    //     setError('Failed to fetch threads. Please try again.');
    //   } finally {
    //     setIsLoading(false);
    //   }
    // }, [hasMore, localSearchTerm, setThreads, page]);


    const fetchThreads = useCallback(async (pageToFetch = page, searchTerm = localSearchTerm) => {
          if (!hasMore) return;
          setIsLoading(true);
      
          // build params, omit `search` if blank
          const params = { page: pageToFetch, limit: 20 };
          if (searchTerm && searchTerm.trim() !== '') {
           params.search = searchTerm.trim();
         }
     
        try {
           const response = await api.get('/threads', { params });
           const newThreads = response.data.map(thread => ({
             ...thread,
             // Use the server-joined customer name directly; the lazy
             // per-thread lookup remains as a fallback for missing ones
             customerName: (thread.name && thread.name !== '0') ? thread.name : null,
             sources: Array.isArray(thread.sources) ? thread.sources : []
          }));
      
           setThreads(prev => {
             const combined = [...prev, ...newThreads];
             const uniqueByPhone = new Map(combined.map(t => [normalizePhoneNumber(t.phone_number), t]));
            return Array.from(uniqueByPhone.values())
                         .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
           });
      
           setHasMore(newThreads.length === 20);
           setPage(pageToFetch + 1);
           setError(null);
        } catch (err) {
            // if upstream proxy returns 502, assume “no more pages”
           if (err.response?.status === 502) {
          console.warn('fetchThreads got 502 — disabling further fetches');
            setHasMore(false);
          } else {
             console.error('Error fetching threads:', err);
            setError('Failed to fetch threads. Please try again.');
          }
        } finally {
          setIsLoading(false);
       }
     }, [hasMore, localSearchTerm, setThreads, page]);

    // Fetch Messages
    const fetchMessages = useCallback(async (phoneNumber) => {
      try {
          const response = await api.get(`/messages/${phoneNumber}`);
          setMessages(prevMessages => {
              console.log('Previous messages:', prevMessages.length);
              console.log('New messages from API:', response.data.length);

              const newMessages = response.data.filter(newMsg =>
                  !prevMessages.some(prevMsg =>
                      prevMsg.id === newMsg.id ||
                      (prevMsg.body === newMsg.body &&
                          Math.abs(new Date(prevMsg.received_at) - new Date(newMsg.received_at)) < 5000)
                  )
              );
              
              // Move this log AFTER newMessages is defined
              console.log('Filtered new messages:', newMessages.length);
              
              return [...prevMessages, ...newMessages];
          });
      } catch (error) {
          console.error('Error fetching messages:', error);
          setError('Failed to fetch messages. Please try again.');
      }
  }, [setMessages]);

    // Refresh Messages
    const refreshMessages = useCallback(() => {
      if (selectedThread) {
        fetchMessages(selectedThread.phone_number);
      }
    }, [selectedThread, fetchMessages]);

    // Update Thread Function (Refactored)
    const updateThread = useCallback(async (phoneNumber, lastMessage, timestamp, isUnreadIncoming = false) => {
      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      const incomingTimestamp = new Date(timestamp);
    
      console.log(`Updating thread for phone: ${normalizedPhone} with message: "${lastMessage}" at ${timestamp}`);
    
      // Get customer info immediately
      const customerInfo = await fetchCustomerInfo(phoneNumber);
    
      setThreads(prevThreads => {
        const threadIndex = prevThreads.findIndex(thread => 
          normalizePhoneNumber(thread.phone_number) === normalizedPhone
        );
    
        if (threadIndex !== -1) {
          const existingThread = prevThreads[threadIndex];
          const existingTimestamp = new Date(existingThread.timestamp);
    
          if (incomingTimestamp > existingTimestamp) {
            const updatedThread = {
              ...existingThread,
              last_message: lastMessage,
              timestamp: timestamp,
              customerName: customerInfo?.name || existingThread.customerName,
              // Only increment unread count for incoming messages (not outbound)
              unread_count: (Number(existingThread.unread_count) || 0) + (isUnreadIncoming ? 1 : 0)
            };
            return [
              updatedThread,
              ...prevThreads.slice(0, threadIndex),
              ...prevThreads.slice(threadIndex + 1)
            ];
          }
          return prevThreads;
        } else {
          const newThread = {
            phone_number: phoneNumber,
            last_message: lastMessage,
            timestamp: timestamp,
            customerName: customerInfo?.name,
            unread_count: isUnreadIncoming ? 1 : 0
          };
          return [newThread, ...prevThreads];
        }
      });
    }, [setThreads, fetchCustomerInfo]);

    // Handle Outbound Message
    const handleOutboundMessage = useCallback(async (message) => {
      console.log('Outbound message received:', message);
    
      // Identify the thread phone number for outbound message
      const threadPhoneNumber = message.to_number;
      const normalizedThreadPhone = normalizePhoneNumber(threadPhoneNumber);
    
      // Fetch customer info if name is not provided (optional logic)
      let customerName = message.customerName;
      if (!customerName) {
        const customerInfo = await fetchCustomerInfo(threadPhoneNumber);
        customerName = customerInfo ? customerInfo.name : null;
      }
    
      // Update the thread list so last_message and timestamp show correctly
      updateThread(threadPhoneNumber, message.body, message.received_at);
    
      // Only add the new outbound message to our 'messages' if this phone matches the selected thread
      if (selectedThread && normalizePhoneNumber(selectedThread.phone_number) === normalizedThreadPhone) {
        setMessages(prevMessages => {
          const messageExists = prevMessages.some(msg =>
            msg.id === message.id ||
            (msg.body === message.body &&
              Math.abs(new Date(msg.received_at) - new Date(message.received_at)) < 5000)
          );
    
          if (messageExists) {
            console.log('Message already exists in state, not adding');
            return prevMessages;
          }
    
          console.log('Adding new message to state');
          return [...prevMessages, message].sort((a, b) => 
            new Date(a.received_at) - new Date(b.received_at)
          );
        });
      }
  }, [
      selectedThread,
      setMessages,
      updateThread,
      fetchCustomerInfo
  ]);

    // Handle New Message
    const handleNewMessage = useCallback((message) => {
      // Add message ID tracking
      const messageKey = `${message.id}-${message.received_at}`;
      if (processedMessages.current.has(messageKey)) {
        console.log('Txtr: Message already processed:', messageKey);
        return;
      }
      processedMessages.current.add(messageKey);

      // Determine if this message belongs to the currently selected thread
      const isOutbound = normalizePhoneNumber(message.from_number) === normalizePhoneNumber(process.env.REACT_APP_TWILIO_PHONE_NUMBER);
      const isUnreadIncoming = !isOutbound && message.is_read !== true;
      const messageThread = isOutbound
      ? normalizePhoneNumber(message.to_number)
      : normalizePhoneNumber(message.from_number);
      
      const belongsToSelectedThread = selectedThread && 
          normalizePhoneNumber(selectedThread.phone_number) === messageThread;
    
      // Only add message to current thread if it belongs there
      if (belongsToSelectedThread) {
        setMessages(prevMessages => {
          const messageExists = prevMessages.some(msg =>
            msg.id === message.id ||
            (msg.body === message.body &&
              Math.abs(new Date(msg.received_at) - new Date(message.received_at)) < 5000)
          );
    
          if (messageExists) {
            return prevMessages;
          }
    
          return [...prevMessages, message].sort((a, b) =>
            new Date(a.received_at) - new Date(b.received_at)
          );
        });
      }
    
      // Always update the thread list
      const threadPhone = isOutbound
        ? message.to_number 
        : message.from_number;
        
      updateThread(threadPhone, message.body, message.received_at, isUnreadIncoming);
    
    // Handle notifications for new messages
    if (isUnreadIncoming) {
      playSound();
      setAlertSoundsAllowed(true);
    }
    }, [selectedThread, setMessages, updateThread, playSound, setAlertSoundsAllowed]);
    

    // Handle Thread Selection
    const handleThreadSelect = useCallback(async (thread) => {
      setSelectedThread(thread);
      setError(null);
      setMessages([]);
      setAlertSoundsAllowed(false);
      
      // Mark all messages in this thread as read on the server
      try {
        await api.put(`/threads/${thread.phone_number}/read`);
        console.log(`Marked thread ${thread.phone_number} as read on server`);
        
        // Refresh threads to get updated unread counts from server
        setThreads(prevThreads => prevThreads.map(t => 
          normalizePhoneNumber(t.phone_number) === normalizePhoneNumber(thread.phone_number)
            ? { ...t, unread_count: 0 }
            : t
        ));
      } catch (error) {
        console.error('Error marking thread as read:', error);
      }
      
      fetchMessages(thread.phone_number);
  }, [fetchMessages, setThreads]);

    // Add New Thread
    const addNewThread = useCallback((newThread) => {
      setThreads(prevThreads => {
        const normalizedPhone = normalizePhoneNumber(newThread.phone_number);
        const threadIndex = prevThreads.findIndex(thread => normalizePhoneNumber(thread.phone_number) === normalizedPhone);

        if (threadIndex !== -1) {
          const existingThread = prevThreads[threadIndex];
          const incomingTimestamp = new Date(newThread.timestamp);
          const existingTimestamp = new Date(existingThread.timestamp);

          if (incomingTimestamp > existingTimestamp) {
            const updatedThread = {
              ...existingThread,
              last_message: newThread.last_message,
              timestamp: newThread.timestamp,
              customerName: newThread.customerName || existingThread.customerName,
            };
            const updatedThreads = [
              updatedThread,
              ...prevThreads.slice(0, threadIndex),
              ...prevThreads.slice(threadIndex + 1)
            ];
            return updatedThreads;
          }
        }

        // If thread does not exist or incoming message is older, add it to the top
        return [newThread, ...prevThreads];
      });
      setSelectedThread(newThread);
      setMessages([]);
    }, [setThreads, setSelectedThread, setMessages]);

    // Filtered Threads based on Search Term
    const filteredThreads = useMemo(() => {
      if (!threads) return [];
      const searchTermLower = localSearchTerm.toLowerCase().trim();
      const containsLetters = /[a-zA-Z]/.test(searchTermLower);
      const phoneNumberMatch = searchTermLower.replace(/\D/g, '');
    
      // Get dates for comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const twoWeeksAgo = new Date(today);
      twoWeeksAgo.setDate(today.getDate() - 14);
    
      const filtered = threads.filter((thread) => {
        if (!thread || !thread.phone_number) return false;
        
        // Only include threads from last 14 days
        const threadDate = new Date(thread.timestamp);
        if (threadDate < twoWeeksAgo) return false;
    
        const normalizedThreadPhone = normalizePhoneNumber(thread.phone_number);
    
        if (containsLetters) {
          if (thread.customerName) {
            const customerNameLower = thread.customerName.toLowerCase();
            const searchTerms = searchTermLower.split(/\s+/);
            return searchTerms.every(term => customerNameLower.includes(term));
          }
          if (!thread.customerInfo_fetching) {
            thread.customerInfo_fetching = true;
            fetchCustomerInfo(thread.phone_number).then(info => {
              if (info && info.name) {
                setThreads(current => 
                  current.map(t => 
                    normalizePhoneNumber(t.phone_number) === normalizedThreadPhone
                      ? { ...t, customerName: info.name }
                      : t
                  )
                );
              }
            });
          }
          return false;
        }
    
        return normalizedThreadPhone.includes(phoneNumberMatch);
      });
    
      // unacknowledged web orders pin to the top, newest first within groups
      return filtered.sort((a, b) =>
        (Number(b.unread_orders) > 0) - (Number(a.unread_orders) > 0) ||
        new Date(b.timestamp) - new Date(a.timestamp)
      );
    }, [threads, localSearchTerm, fetchCustomerInfo, setThreads]);

    // Expose Methods to Parent via Ref
    useImperativeHandle(ref, () => ({
      updateSearchTerm: (term) => {
        setLocalSearchTerm(term);
      },
      addNewThread,
      refreshMessages,
      addNewMessage: (message) => {
        setMessages(prevMessages => {
          // Check if the message already exists in the state
          const messageExists = prevMessages.some(msg =>
            msg.id === message.id ||
            (msg.body === message.body &&
              Math.abs(new Date(msg.received_at) - new Date(message.received_at)) < 5000)
          );

          if (messageExists) {
            console.log('Message already exists in state, not adding');
            return prevMessages;
          }

          console.log('Adding new message to state');
          return [...prevMessages, message];
        });
      },
      updateThread
    }));

    // Effect: Show Welcome Popup and Initialize Audio
    useEffect(() => {
      if (showWelcomePopup) {
        const timer = setTimeout(() => {
          initializeAudio();
          enableAudio();
          setShowWelcomePopup(false);
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [showWelcomePopup, enableAudio, initializeAudio]);

    // Effect: Fetch Threads and Setup Socket Listeners
    useEffect(() => {
      fetchThreads();
      setIsTxtrLoaded(true);
      socket.on('newMessage', handleNewMessage);

      return () => {
        setIsTxtrLoaded(false);
        socket.off('newMessage', handleNewMessage);
      };
    }, [fetchThreads, setIsTxtrLoaded, handleNewMessage]);

    // Effect: Setup Outbound Message Listener
    useEffect(() => {
      socket.on('outboundMessage', handleOutboundMessage);

      return () => {
        socket.off('outboundMessage', handleOutboundMessage);
      };
    }, [handleOutboundMessage]);

    // Effect: Fetch Messages when Selected Thread Changes (causing double fetch)
    // useEffect(() => {
    //   if (selectedThread) {
    //     fetchMessages(selectedThread.phone_number);
    //   }
    // }, [selectedThread, fetchMessages]);

    // Effect: Scroll to Bottom on Messages Update
    useEffect(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      }
    }, [messages]);

    // Effect: Calculate total unread count whenever threads change
    useEffect(() => {
      const total = threads.reduce((sum, thread) => sum + (parseInt(thread.unread_count) || 0), 0);
      setTotalUnreadCount(total);
    }, [threads]);

    // Effect: Setup Repeating Alert Sound
    useEffect(() => {
      // Only set up alert sounds if allowed, there are unread messages, and audio is enabled
      if (alertSoundsAllowed && totalUnreadCount > 0 && isAudioEnabled) {
        if (!alertIntervalRef.current) {
          // Play the sound immediately upon detecting unread messages
          playSound();

          // Set up the interval to play sound every minute
          alertIntervalRef.current = setInterval(() => {
            playSound();
          }, 60000);
        }
      } else {
        // If no unread messages or alert sounds are not allowed, clear the interval
        if (alertIntervalRef.current) {
          clearInterval(alertIntervalRef.current);
          alertIntervalRef.current = null;
        }
      }

      // Cleanup on unmount or when dependencies change
      return () => {
        if (alertIntervalRef.current) {
          clearInterval(alertIntervalRef.current);
          alertIntervalRef.current = null;
        }
      };
    }, [alertSoundsAllowed, totalUnreadCount, isAudioEnabled, playSound]);

    // Effect: Stop Alerts on User Interaction
    useEffect(() => {
      const handleUserInteraction = () => {
        if (alertSoundsAllowed && totalUnreadCount > 0) {
          setAlertSoundsAllowed(false); // Stop alert sounds
          if (alertIntervalRef.current) {
            clearInterval(alertIntervalRef.current);
            alertIntervalRef.current = null;
          }
        }
      };

      window.addEventListener('click', handleUserInteraction);
      window.addEventListener('keydown', handleUserInteraction);
      window.addEventListener('touchstart', handleUserInteraction); // For mobile devices

      return () => {
        window.removeEventListener('click', handleUserInteraction);
        window.removeEventListener('keydown', handleUserInteraction);
        window.removeEventListener('touchstart', handleUserInteraction);
      };
    }, [alertSoundsAllowed, totalUnreadCount]);

    // Attachment Rendering Helper Functions
    const getAttachmentUrl = (filename) => {
      return `/uploads/${filename}`;
    };

    const renderAttachment = (attachment) => {
      if (!attachment || !attachment.filename) {
        console.error('Invalid attachment:', attachment);
        return null;
      }

      const attachmentUrl = getAttachmentUrl(attachment.filename);

      if (attachment.contentType.startsWith('image/')) {
        return (
          <>
            <img
              src={attachmentUrl}
              alt={attachment.filename}
              className="max-w-full h-auto cursor-pointer"
              onClick={() => setSelectedImage(attachmentUrl)}
              onError={(e) => {
                console.error('Image load error:', e);
                e.target.onerror = null;
                e.target.src = '/path/to/fallback/image.png';
              }}
            />
            {selectedImage && (
              <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setSelectedImage(null)}>
                <img src={selectedImage} alt="Full size" className="max-w-full max-h-full" />
              </div>
            )}
          </>
        );
      } else if (attachment.contentType.startsWith('video/')) {
        return (
          <video src={attachmentUrl} controls className="max-w-full h-auto">
            Your browser does not support the video tag.
          </video>
        );
      } else if (attachment.contentType.startsWith('audio/')) {
        return (
          <audio src={attachmentUrl} controls className="w-full">
            Your browser does not support the audio tag.
          </audio>
        );
      } else {
        return (
          <a href={attachmentUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline block">
            Download {attachment.filename}
          </a>
        );
      }
    };

    // Render Welcome Popup if Needed
    if (showWelcomePopup) {
      return <WelcomePopup onClose={() => setShowWelcomePopup(false)} />;
    }

    // Main Render
    return (
      <div className="flex h-full bg-white pt-4 pb-20">
        <div className="w-1/3 border-r border-gray-300 h-full overflow-y-auto">
          {totalUnreadCount > 0 && (
            <UnreadAlert
              unreadCount={totalUnreadCount}
              onClearAll={clearAllUnread}
            />
          )}
          {filteredThreads.map((thread, index) => (
          <div
            key={normalizePhoneNumber(thread.phone_number)}
            ref={node => {
              if (node) {
                node.dataset.thread = JSON.stringify(thread);
                threadObserver.current.observe(node);
              }
              if (index === filteredThreads.length - 1) lastThreadElementRef(node);
            }}
            className={`p-4 border-b border-gray-200 cursor-pointer ${
              thread.has_order ? 'border-l-4 border-l-red-500' : ''
            } ${
              Number(thread.unread_orders) > 0 ? 'bg-red-50 animate-pulse' : ''
            } ${
              selectedThread?.phone_number === thread.phone_number
                ? 'bg-blue-100'
                : 'hover:bg-gray-100'
            } ${
              new Date(thread.timestamp).toDateString() === new Date().toDateString()
                ? 'opacity-100'
                : 'opacity-70'
            }`}
            onClick={() => handleThreadSelect(thread)}
          >
            <div className="flex items-center">
              {Number(thread.unread_count) > 0 && (
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0"></div>
              )}
            <div className="font-semibold text-gray-700 truncate flex-grow">
              {(thread.customerName && thread.customerName !== '0' && thread.customerName !== 0) ? thread.customerName : formatPhoneNumber(thread.phone_number)}
              {Number(thread.unread_count) > 0 && (
                <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                  {thread.unread_count}
                </span>
              )}
            </div>
            </div>
            <div className="flex items-center gap-1.5">
              {(thread.customerName && thread.customerName !== '0' && thread.customerName !== 0) && (
                <span className="text-sm text-gray-600">{formatPhoneNumber(thread.phone_number)}</span>
              )}
              {Array.isArray(thread.sources) && thread.sources.map(src => (
                SOURCE_CHIPS[src] ? (
                  <span key={src} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${SOURCE_CHIPS[src].className}`}>
                    {SOURCE_CHIPS[src].label}
                  </span>
                ) : null
              ))}
            </div>
            <div className="text-sm text-gray-600 truncate">{thread.last_message}</div>
            <div className={`text-xs ${
              new Date(thread.timestamp).toDateString() === new Date().toDateString()
                ? 'text-blue-600 font-semibold'
                : 'text-gray-400'
            }`}>
              {new Date(thread.timestamp).toLocaleString()}
            </div>
          </div>
          ))}
          {isLoading && <div className="p-4 text-gray-500">Loading more threads...</div>}
        </div>

        <div className="w-2/3 flex flex-col h-full">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {selectedThread ? (
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                {messages.map((message) => {
                  return (
                    <div
                      key={`${message.id}-${message.received_at}`}
                      className={`mb-4 flex ${
                        normalizePhoneNumber(message.from_number) === normalizePhoneNumber(process.env.REACT_APP_TWILIO_PHONE_NUMBER)
                          ? 'justify-end'
                          : 'justify-start'
                      }`}
                    >
                      <div
                        className={`p-2 rounded-lg max-w-xs ${
                          normalizePhoneNumber(message.from_number) === normalizePhoneNumber(process.env.REACT_APP_TWILIO_PHONE_NUMBER)
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-300 text-black'
                        }`}
                      >
                        {message.body}
                        {message.attachments && message.attachments.length > 0 && (
                          <div>
                            <p>Attachments ({message.attachments.length}):</p>
                            {message.attachments.map((attachment, index) => (
                              <div key={index} className="mt-2">
                                {renderAttachment(attachment)}
                              </div>
                            ))}
                          </div>
                        )}
                        <div
                          className={`text-xs mt-1 ${
                            normalizePhoneNumber(message.from_number) === normalizePhoneNumber(process.env.REACT_APP_TWILIO_PHONE_NUMBER)
                              ? 'text-gray-200'
                              : 'text-gray-500'
                          }`}
                        >
                          {new Date(message.received_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center flex-1 text-gray-500">
              Select a thread to start chatting
            </div>
          )}
        </div>
      </div>
    );

  });

  export default Txtr;
